import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import { levelStyle } from "@/lib/tcfLevel";
import {
  BookOpen, Headphones, ArrowRight, ArrowLeft, Loader2, CheckCircle2, XCircle,
  RotateCcw, Trophy, Lock, Crown, Layers, X, ChevronRight, LayoutGrid, Flag, Check, Target, Sparkles,
} from "lucide-react";

const MEDIA = (p) => `${process.env.REACT_APP_BACKEND_URL}/api/exams/media/${p}`;
const LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2"];
const SKILL_META = {
  reading: { label: "Reading", fr: "Compréhension écrite", icon: BookOpen },
  listening: { label: "Listening", fr: "Compréhension orale", icon: Headphones },
};

function Palette({ questions, current, answers, flagged, onJump, gridClass = "grid-cols-5" }) {
  return (
    <div data-testid="lp-palette">
      <div className="flex items-center gap-2 text-sm font-semibold"><LayoutGrid className="h-4 w-4 text-primary" /> Questions</div>
      <div className={`mt-3 grid ${gridClass} gap-1.5`}>
        {questions.map((q, i) => {
          let cls = "bg-secondary text-muted-foreground";
          if (flagged.has(q.id)) cls = "bg-amber-400 text-amber-950";
          else if (answers[q.id]) cls = "emerald-surface text-white";
          const ring = i === current ? "ring-2 ring-offset-1 ring-primary" : "";
          return (
            <button key={q.id} onClick={() => onJump(i)} data-testid={`lp-palette-${i + 1}`}
              className={`h-9 rounded-lg text-xs font-bold transition-all ${cls} ${ring}`}>{i + 1}</button>
          );
        })}
      </div>
      <div className="mt-4 space-y-1.5 text-xs text-muted-foreground">
        <div className="flex items-center gap-2"><span className="h-3 w-3 rounded emerald-surface inline-block" /> Answered</div>
        <div className="flex items-center gap-2"><span className="h-3 w-3 rounded bg-amber-400 inline-block" /> Marked</div>
        <div className="flex items-center gap-2"><span className="h-3 w-3 rounded bg-secondary inline-block" /> Not answered</div>
      </div>
    </div>
  );
}

function Transcript({ text, highlight }) {
  if (!text) return null;
  if (highlight && text.includes(highlight)) {
    const parts = text.split(highlight);
    return (
      <p className="text-sm text-muted-foreground italic leading-relaxed">
        {parts.map((p, i) => (
          <React.Fragment key={i}>
            {p}
            {i < parts.length - 1 && <mark className="bg-amber-200 text-amber-900 rounded px-0.5 not-italic font-medium">{highlight}</mark>}
          </React.Fragment>
        ))}
      </p>
    );
  }
  return <p className="text-sm text-muted-foreground italic leading-relaxed">{text}</p>;
}

export default function LevelPractice() {
  const [params, setParams] = useSearchParams();
  const skill = ["reading", "listening"].includes(params.get("skill")) ? params.get("skill") : "reading";
  const setSkill = (s) => setParams({ skill: s }, { replace: true });

  const [step, setStep] = useState("levels"); // levels | sets | quiz | results
  const [overview, setOverview] = useState(null);
  const [coaching, setCoaching] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const [batch, setBatch] = useState(20);

  const [level, setLevel] = useState(null);
  const [sets, setSets] = useState([]);
  const [setsLoading, setSetsLoading] = useState(false);

  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [flagged, setFlagged] = useState(new Set());
  const [current, setCurrent] = useState(0);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [zoom, setZoom] = useState(null);
  const [showPalette, setShowPalette] = useState(false);

  const loadOverview = () => {
    setLoading(true);
    Promise.all([
      api.get("/level-practice/overview", { params: { skill } }),
      api.get("/level-practice/coaching", { params: { skill } }).catch(() => ({ data: null })),
    ])
      .then(([ov, co]) => { setOverview(ov.data); setBatch(ov.data.batch || 20); setCoaching(co?.data || null); setLocked(false); })
      .catch((e) => { if (e.response?.status === 403) setLocked(true); else toast.error("Could not load practice."); })
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setStep("levels"); loadOverview(); }, [skill]);

  const openSets = (lvl) => {
    setLevel(lvl); setStep("sets"); setSetsLoading(true); setSets([]); window.scrollTo(0, 0);
    api.get("/level-practice/sets", { params: { skill, level: lvl } })
      .then((r) => { setSets(r.data.sets || []); setTotal(r.data.total || 0); setBatch(r.data.batch || 20); })
      .catch((e) => { if (e.response?.status === 403) setLocked(true); else toast.error("Could not load sets."); })
      .finally(() => setSetsLoading(false));
  };

  const startSet = async (lvl, off = 0) => {
    setBusy(true);
    try {
      const { data } = await api.get("/level-practice/questions", { params: { skill, level: lvl, offset: off } });
      if (!data.questions.length) { toast.error("No questions available here yet."); return; }
      setLevel(lvl); setOffset(data.offset); setTotal(data.total); setHasMore(data.has_more);
      setQuestions(data.questions); setAnswers({}); setFlagged(new Set()); setCurrent(0); setResult(null);
      setStep("quiz"); window.scrollTo(0, 0);
    } catch (e) {
      if (e.response?.status === 403) setLocked(true); else toast.error("Could not load questions.");
    } finally { setBusy(false); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/level-practice/submit", { skill, level, offset, answers });
      setResult(data); setStep("results"); setShowPalette(false); window.scrollTo(0, 0);
    } catch (e) { toast.error("Could not submit your answers."); }
    finally { setBusy(false); }
  };

  const toggleFlag = (qid) => setFlagged((f) => { const n = new Set(f); n.has(qid) ? n.delete(qid) : n.add(qid); return n; });
  const jump = (i) => { setCurrent(Math.max(0, Math.min(questions.length - 1, i))); setShowPalette(false); window.scrollTo({ top: 0 }); };

  const SkillToggle = () => (
    <div className="inline-flex rounded-full bg-secondary p-1" data-testid="lp-skill-toggle">
      {Object.entries(SKILL_META).map(([k, m]) => {
        const SI = m.icon;
        const active = skill === k;
        return (
          <button key={k} onClick={() => setSkill(k)} data-testid={`lp-skill-${k}`}
            className={`inline-flex items-center gap-2 px-4 sm:px-5 h-10 rounded-full text-sm font-semibold transition-colors ${active ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground hover:text-foreground"}`}>
            <SI className="h-4 w-4" /> {m.label}
          </button>
        );
      })}
    </div>
  );

  // ---------------- LOCKED ----------------
  if (locked) {
    return (
      <div data-testid="lp-locked">
        <h1 className="font-display text-4xl font-semibold flex items-center gap-3"><Layers className="h-8 w-8 text-primary" /> Practice by level</h1>
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="mt-6 rounded-2xl emerald-surface grain text-white p-6 sm:p-7 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="h-12 w-12 rounded-2xl bg-white/15 grid place-items-center shrink-0"><Lock className="h-6 w-6" /></div>
          <div className="flex-1">
            <h2 className="font-display text-xl sm:text-2xl font-semibold">Drill every A1 → C2 question — go Premium</h2>
            <p className="text-white/85 mt-1 text-sm">Practice by level pulls from all 80 real TCF mock exams. Unlock it with Premium — early-launch price <span className="font-semibold">$15 CAD/month</span> <span className="line-through opacity-70">$29</span>.</p>
          </div>
          <Link to="/premium" data-testid="lp-upgrade-btn" className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-white text-primary font-semibold shrink-0">
            <Crown className="h-4 w-4" /> Go Premium <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.div>
      </div>
    );
  }

  // ---------------- LEVELS HUB ----------------
  if (step === "levels") {
    const byLevel = {};
    (overview?.levels || []).forEach((l) => { byLevel[l.level] = l; });
    return (
      <div data-testid="lp-levels">
        <Link to="/tests" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1"><ArrowLeft className="h-4 w-4" /> Back to Tests</Link>
        <h1 className="font-display text-4xl font-semibold flex items-center gap-3 mt-2"><Layers className="h-8 w-8 text-primary" /> Practice by level</h1>
        <p className="text-muted-foreground mt-2">Drill every {SKILL_META[skill].fr} question by CEFR level, in test order, {batch} at a time — and see your score for each question type.</p>

        <div className="mt-6"><SkillToggle /></div>

        {!loading && coaching && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="lp-coach-card"
            className="mt-6 rounded-2xl border border-primary/30 bg-accent/60 p-5 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="h-11 w-11 rounded-xl emerald-surface grid place-items-center shrink-0"><Target className="h-5 w-5 text-white" /></div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-primary flex items-center gap-1.5"><Sparkles className="h-4 w-4" /> Coach's pick</p>
              <p className="text-sm text-foreground mt-0.5" data-testid="lp-coach-message">{coaching.message}</p>
            </div>
            {coaching.recommendation && (
              <button onClick={() => startSet(coaching.recommendation.level, coaching.recommendation.offset)} data-testid="lp-coach-drill-btn"
                className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-primary text-primary-foreground font-semibold shrink-0 hover:opacity-90 transition-opacity">
                Drill {coaching.recommendation.level} · Set {coaching.recommendation.set_index} <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </motion.div>
        )}

        {loading ? (
          <p className="text-muted-foreground mt-8">Loading…</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
            {LEVELS.map((lvl, i) => {
              const d = byLevel[lvl] || { level: lvl, total_questions: 0, sessions: 0, last_pct: null, best_pct: null, points: null };
              const st = levelStyle(lvl);
              return (
                <motion.button
                  key={lvl} onClick={() => openSets(lvl)} disabled={!d.total_questions}
                  initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                  data-testid={`lp-level-${lvl}`}
                  className="group text-left rounded-2xl bg-card border border-border p-6 hover:shadow-lg hover:-translate-y-1 transition-all disabled:opacity-50 disabled:hover:translate-y-0 disabled:cursor-not-allowed">
                  <div className="flex items-center justify-between">
                    <span className={`inline-flex items-center justify-center h-11 min-w-11 px-3 rounded-xl border font-bold text-lg ${st.chip}`}>{lvl}</span>
                    {d.points != null && <span className="text-xs text-muted-foreground">{d.points} pts / question</span>}
                  </div>
                  <h3 className="mt-4 font-semibold text-lg">{d.total_questions} questions</h3>
                  <p className="text-sm text-muted-foreground mt-0.5">{d.batches || 0} sets of {batch}</p>

                  {(d.last_pct != null || d.best_pct != null) ? (
                    <div className="mt-4 space-y-2" data-testid={`lp-level-score-${lvl}`}>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="text-muted-foreground">Last <span className="font-bold text-foreground">{d.last_pct ?? "—"}%</span></span>
                        <span className="text-muted-foreground">Best <span className="font-bold text-foreground">{d.best_pct ?? "—"}%</span></span>
                        <span className="text-muted-foreground ml-auto">{d.sessions} session{d.sessions === 1 ? "" : "s"}</span>
                      </div>
                      <div className="h-2 rounded-full bg-secondary overflow-hidden">
                        <div className={`h-full ${st.bar}`} style={{ width: `${d.best_pct ?? 0}%` }} />
                      </div>
                    </div>
                  ) : (
                    <p className="mt-4 text-xs text-muted-foreground">Not practised yet</p>
                  )}

                  <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary">
                    Choose a set <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </motion.button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  const st = level ? levelStyle(level) : levelStyle("A1");
  const isListening = skill === "listening";

  // ---------------- SETS PICKER ----------------
  if (step === "sets") {
    const nSets = sets.length;
    return (
      <div data-testid="lp-sets">
        <button onClick={() => { setStep("levels"); loadOverview(); }} data-testid="lp-sets-back"
          className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1"><ArrowLeft className="h-4 w-4" /> All levels</button>
        <div className="flex items-center gap-3 mt-2">
          <span className={`inline-flex items-center justify-center h-11 min-w-11 px-3 rounded-xl border font-bold text-lg ${st.chip}`}>{level}</span>
          <div>
            <h1 className="font-display text-3xl font-semibold">{SKILL_META[skill].label} · Level {level}</h1>
            <p className="text-muted-foreground text-sm">{total} questions · {nSets} sets of {batch} · pick a set to practise.</p>
          </div>
        </div>

        {setsLoading ? (
          <p className="text-muted-foreground mt-8">Loading sets…</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
            {sets.map((s, i) => {
              const done = s.completed;
              return (
                <motion.button key={s.index} onClick={() => startSet(level, s.offset)} disabled={busy}
                  initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}
                  data-testid={`lp-set-${s.index}`}
                  className="group text-left rounded-2xl bg-card border border-border p-5 hover:shadow-lg hover:-translate-y-1 transition-all disabled:opacity-60">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-lg">Set {s.index}</h3>
                    {done ? (
                      <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full border ${st.chip}`} data-testid={`lp-set-score-${s.index}`}>
                        <Check className="h-3 w-3" /> {s.best_pct}%
                      </span>
                    ) : (
                      <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">Not started</span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">Questions {s.start}–{s.end}</p>
                  {done ? (
                    <div className="mt-3">
                      <div className="h-2 rounded-full bg-secondary overflow-hidden">
                        <div className={`h-full ${st.bar}`} style={{ width: `${s.best_pct ?? 0}%` }} />
                      </div>
                      <p className="mt-1.5 text-[11px] text-muted-foreground">Best {s.best_pct}% · last {s.last_pct}% · {s.attempts} attempt{s.attempts === 1 ? "" : "s"}</p>
                    </div>
                  ) : (
                    <p className="mt-3 text-[11px] text-muted-foreground">{s.size} questions</p>
                  )}
                  <div className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary">
                    {done ? "Retry set" : "Start set"} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </motion.button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ---------------- RESULTS ----------------
  if (step === "results" && result) {
    return (
      <div data-testid="lp-results">
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
          className="rounded-3xl emerald-surface grain relative overflow-hidden p-8 text-center">
          <Trophy className="h-10 w-10 mx-auto text-white/90" />
          <div className="mt-3 text-6xl font-display font-semibold text-white" data-testid="lp-score">{result.pct}%</div>
          <p className="mt-2 text-white/85">{result.correct} / {result.total} correct · {SKILL_META[skill].label} {level}</p>
          <p className="mt-1 text-white/70 text-sm">Set {Math.floor(offset / batch) + 1} · Questions {offset + 1}–{offset + result.total} of {total}</p>
        </motion.div>

        <div className="mt-6 flex flex-wrap gap-3" data-testid="lp-results-actions">
          {hasMore && (
            <button onClick={() => startSet(level, offset + questions.length)} disabled={busy} data-testid="lp-next-set-btn"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-opacity disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Next set <ChevronRight className="h-4 w-4" /></>}
            </button>
          )}
          <button onClick={() => startSet(level, offset)} disabled={busy} data-testid="lp-retry-btn"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-border font-semibold hover:bg-secondary transition-colors">
            <RotateCcw className="h-4 w-4" /> Retry this set
          </button>
          <button onClick={() => openSets(level)} data-testid="lp-back-sets-btn"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-border font-semibold hover:bg-secondary transition-colors">
            <LayoutGrid className="h-4 w-4" /> All sets
          </button>
          <button onClick={() => { setStep("levels"); loadOverview(); }} data-testid="lp-back-levels-btn"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-border font-semibold hover:bg-secondary transition-colors">
            <Layers className="h-4 w-4" /> All levels
          </button>
        </div>

        <div className="mt-8 space-y-4">
          {result.review.map((r, i) => (
            <div key={r.id} className="rounded-2xl bg-card border border-border p-5" data-testid={`lp-review-${i}`}>
              <div className="flex items-start gap-3">
                {r.is_correct ? <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" /> : <XCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="text-xs font-semibold text-muted-foreground">Q{offset + i + 1}</span>
                    {r.source_title && <span className="text-xs text-muted-foreground">· {r.source_title}</span>}
                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full border ${st.chip}`}>{level}</span>
                  </div>
                  {r.image_paths?.map((p) => (
                    <img key={p} src={MEDIA(p)} alt={`question ${r.number}`} onClick={() => setZoom(MEDIA(p))}
                      className="mb-3 rounded-xl border border-border max-w-full max-h-[42vh] object-contain cursor-zoom-in"
                      draggable={false} onContextMenu={(e) => e.preventDefault()} />
                  ))}
                  {r.audio_paths?.map((p) => (
                    <div key={p} className="mb-3 flex items-center gap-2"><Headphones className="h-4 w-4 text-primary shrink-0" /><audio controls src={MEDIA(p)} className="w-full" /></div>
                  ))}
                  <p className="font-medium">{r.question_text}</p>
                  <div className="mt-3 space-y-1.5">
                    {r.options.map((opt) => {
                      const isCorrect = opt.id === r.correct_answer;
                      const isChosen = opt.id === r.selected;
                      return (
                        <div key={opt.id} className={`text-sm px-3 py-2 rounded-lg border flex items-center gap-2 ${
                          isCorrect ? "border-primary bg-accent text-accent-foreground" :
                          isChosen ? "border-destructive bg-destructive/10" : "border-transparent"}`}>
                          <span className="font-bold">{opt.id}</span>
                          <span>{opt.text}</span>
                          {isCorrect && <span className="ml-auto text-xs font-semibold text-primary">✓ correct</span>}
                          {isChosen && !isCorrect && <span className="ml-auto text-xs font-semibold text-destructive">your choice</span>}
                        </div>
                      );
                    })}
                  </div>
                  {isListening && r.transcript && (
                    <div className="mt-3 rounded-lg bg-secondary/60 p-3"><Transcript text={r.transcript} highlight={r.answer_highlight} /></div>
                  )}
                  {r.explanation && <p className="mt-3 text-sm text-muted-foreground"><span className="font-semibold text-foreground">Why: </span>{r.explanation}</p>}
                </div>
              </div>
            </div>
          ))}
        </div>

        {zoom && (
          <div className="fixed inset-0 z-50 bg-black/80 grid place-items-center p-4" onClick={() => setZoom(null)} data-testid="lp-zoom">
            <button className="absolute top-4 right-4 h-10 w-10 grid place-items-center rounded-full bg-white/15 text-white"><X className="h-5 w-5" /></button>
            <img src={zoom} alt="enlarged" className="max-w-full max-h-full object-contain rounded-lg" draggable={false} onContextMenu={(e) => e.preventDefault()} />
          </div>
        )}
      </div>
    );
  }

  // ---------------- QUIZ ----------------
  const q = questions[current];
  const answeredCount = Object.keys(answers).length;
  const setIndex = Math.floor(offset / batch) + 1;
  return (
    <div data-testid="lp-quiz">
      {/* Top bar */}
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <button onClick={() => openSets(level)} data-testid="lp-exit-btn"
          className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"><ArrowLeft className="h-4 w-4" /> All sets</button>
        <div className="flex items-center gap-2">
          <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${st.chip}`}>{SKILL_META[skill].label} · {level} · Set {setIndex}</span>
          <button onClick={() => setShowPalette(true)} data-testid="lp-open-palette-btn" className="lg:hidden inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-secondary text-sm font-semibold"><LayoutGrid className="h-4 w-4" /> {answeredCount}/{questions.length}</button>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-[260px_1fr] items-start">
        {/* LEFT SIDEBAR */}
        <div className="hidden lg:flex flex-col gap-4">
          <div className="rounded-2xl bg-card border border-border p-4">
            <Palette questions={questions} current={current} answers={answers} flagged={flagged} onJump={jump} />
            <button onClick={submit} disabled={busy || answeredCount === 0} data-testid="lp-submit-btn-side"
              className="mt-4 w-full h-11 rounded-xl bg-primary text-primary-foreground text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Finish (${answeredCount}/${questions.length})`}
            </button>
          </div>
          <button onClick={() => openSets(level)} data-testid="lp-exit-side-btn"
            className="inline-flex items-center justify-center gap-2 h-11 rounded-xl border border-border text-sm font-semibold hover:bg-secondary"><X className="h-4 w-4" /> Exit to sets</button>
        </div>

        {/* MAIN */}
        <div data-testid={`lp-question-${q.id}`}>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-sm font-semibold text-muted-foreground" data-testid="lp-progress-label">Question {current + 1} / {questions.length}</span>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground hidden sm:inline">Set {setIndex} · Q{offset + 1}–{offset + questions.length} of {total}</span>
              <button onClick={() => toggleFlag(q.id)} data-testid="lp-flag-btn"
                className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-full text-xs font-semibold transition-colors ${flagged.has(q.id) ? "bg-amber-400 text-amber-950" : "bg-secondary text-muted-foreground hover:bg-accent"}`}>
                <Flag className="h-3.5 w-3.5" /> {flagged.has(q.id) ? "Marked" : "Mark"}
              </button>
            </div>
          </div>
          <div className="h-1.5 w-full rounded-full bg-secondary overflow-hidden">
            <div className="h-full bg-primary transition-all" style={{ width: `${((current + 1) / questions.length) * 100}%` }} />
          </div>

          <AnimatePresence mode="wait">
            <motion.div key={q.id} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.25 }} className="mt-5 rounded-2xl bg-card border border-border p-6">
              {q.source_title && <p className="text-xs text-muted-foreground mb-3">{q.source_title}</p>}
              {q.image_paths?.map((p) => (
                <img key={p} src={MEDIA(p)} alt={`question ${q.number}`} onClick={() => setZoom(MEDIA(p))} title="Tap to enlarge"
                  className="mb-4 rounded-xl border border-border max-w-full max-h-[48vh] object-contain cursor-zoom-in mx-auto"
                  draggable={false} onContextMenu={(e) => e.preventDefault()} data-testid={`lp-q-image-${q.id}`} />
              ))}
              {q.audio_paths?.map((p) => (
                <div key={p} className="mb-4 flex items-center gap-2"><Headphones className="h-5 w-5 text-primary shrink-0" /><audio controls src={MEDIA(p)} className="w-full" data-testid={`lp-q-audio-${q.id}`} /></div>
              ))}

              <h2 className="text-lg sm:text-xl font-semibold mb-5">{q.question_text}</h2>

              <div className="grid sm:grid-cols-2 gap-3">
                {q.options.map((opt) => {
                  const selected = answers[q.id] === opt.id;
                  return (
                    <button key={opt.id} onClick={() => setAnswers((a) => ({ ...a, [q.id]: opt.id }))} data-testid={`lp-opt-${q.id}-${opt.id}`}
                      className={`w-full text-left px-4 py-3.5 rounded-xl border transition-all flex items-center gap-3 ${selected ? "border-primary bg-accent" : "border-border bg-background hover:border-primary/40"}`}>
                      <span className={`h-6 w-6 rounded-full grid place-items-center text-xs font-bold shrink-0 ${selected ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>{opt.id}</span>
                      <span>{opt.text}</span>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="mt-6 flex items-center justify-between">
            <button onClick={() => jump(current - 1)} disabled={current === 0} data-testid="lp-prev-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-border font-semibold disabled:opacity-40 hover:bg-secondary transition-colors">
              <ArrowLeft className="h-4 w-4" /> Previous
            </button>

            {current < questions.length - 1 ? (
              <button onClick={() => jump(current + 1)} data-testid="lp-next-btn"
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-opacity">
                Next <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button onClick={submit} disabled={busy || answeredCount === 0} data-testid="lp-submit-btn"
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-opacity disabled:opacity-60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Finish ({answeredCount}/{questions.length})</>}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile palette slide-over */}
      {showPalette && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/40" onClick={() => setShowPalette(false)} data-testid="lp-palette-overlay">
          <div className="absolute right-0 top-0 h-full w-80 max-w-[85vw] bg-card p-5 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <Palette questions={questions} current={current} answers={answers} flagged={flagged} onJump={jump} gridClass="grid-cols-6" />
            <button onClick={submit} disabled={busy || answeredCount === 0} className="mt-4 w-full h-11 rounded-xl bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-60">Finish ({answeredCount}/{questions.length})</button>
          </div>
        </div>
      )}

      {zoom && (
        <div className="fixed inset-0 z-50 bg-black/80 grid place-items-center p-4" onClick={() => setZoom(null)} data-testid="lp-zoom">
          <button className="absolute top-4 right-4 h-10 w-10 grid place-items-center rounded-full bg-white/15 text-white"><X className="h-5 w-5" /></button>
          <img src={zoom} alt="enlarged" className="max-w-full max-h-full object-contain rounded-lg" draggable={false} onContextMenu={(e) => e.preventDefault()} />
        </div>
      )}
    </div>
  );
}
